import { useCallback, useState } from 'react';
import { Verified } from '@mui/icons-material';
import { Chip, FormHelperText, InputAdornment, Typography } from '@mui/material';
import { TenantDomainWritable } from 'api';
import { ResponsiveTextField } from '../../SingleSignOn.styles';
import { SINGLE_SIGN_ON_TEST_IDS } from '../../SingleSignOn.testIds';

const EmailDomains = ({
  emailDomains,
  setEmailDomains,
  isStaffOrSuperuser,
}: {
  emailDomains: TenantDomainWritable[];
  setEmailDomains: React.Dispatch<React.SetStateAction<TenantDomainWritable[]>>;
  isStaffOrSuperuser: boolean;
}) => {
  const [domainInput, setDomainInput] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDomainInput(event.target.value);
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const { value } = event.currentTarget;
        const cleanValue = value?.trim() ?? '';
        const existingDomains = emailDomains.map(domainObj => domainObj.domain.toLowerCase());
        const isDuplicate = existingDomains.includes(cleanValue.toLowerCase());
        if (isDuplicate) {
          setInputError('This domain has already been added.');
          return;
        }
        setInputError('');
        if (cleanValue) {
          setEmailDomains(prevDomains => [
            ...prevDomains,
            { domain: cleanValue.toLowerCase(), id: null, is_verified: false },
          ]);
          setDomainInput('');
        }
      }
    },
    [emailDomains, setEmailDomains]
  );

  const handleChipClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // read value from event to know which domain to toggle
      setEmailDomains(prevDomains => {
        const domainToToggle = event.currentTarget.innerText;
        const domainIndex = prevDomains.findIndex(d => d.domain === domainToToggle);
        if (domainIndex === -1) {
          return prevDomains;
        }
        const updatedDomains = [...prevDomains];
        updatedDomains[domainIndex].is_verified = !updatedDomains[domainIndex].is_verified;
        return updatedDomains;
      });
    },
    [setEmailDomains]
  );

  const handleDelete = useCallback(
    (domain: string) => {
      setEmailDomains(prevDomains => prevDomains.filter(d => d.domain !== domain.toLowerCase()));
    },
    [setEmailDomains]
  );

  return (
    <>
      <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
        Associated email domains
      </Typography>
      <ResponsiveTextField
        id="domain-input"
        data-testid={SINGLE_SIGN_ON_TEST_IDS.emailDomainsInput}
        type="text"
        variant="outlined"
        size="small"
        value={domainInput}
        error={Boolean(inputError)}
        onChange={handleChange}
        helperText={inputError}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                @
              </InputAdornment>
            ),
            onKeyDown: handleKeyDown,
          },
        }}
      />
      <FormHelperText>Press Enter to add domain</FormHelperText>
      <section style={{ marginBottom: 0 }} data-testid={SINGLE_SIGN_ON_TEST_IDS.emailDomainsSection}>
        {emailDomains.map(({ domain, is_verified }) => (
          <Chip
            id={domain}
            variant="outlined"
            color={is_verified ? 'success' : 'default'}
            icon={is_verified ? <Verified /> : undefined}
            label={domain}
            key={domain}
            onClick={isStaffOrSuperuser ? handleChipClick : undefined}
            onDelete={isStaffOrSuperuser ? _ => handleDelete(domain) : undefined}
            sx={{ p: 1.125, m: 1 }}
          />
        ))}
      </section>
    </>
  );
};

export default EmailDomains;
